import React from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import reactStringReplace from 'react-string-replace'
import { createStructuredSelector } from 'reselect'

import { EVENTS, trackEvent } from 'utils/tracking/events-tracker'
import { ThreeDSecureErrorHelperWithProviders } from 'utils/three-d-secure-helper/three-d-secure-error-helper'
import { CHECKOUT_ERRORS } from 'utils/consts.mjs'
import { ThreeDSecureContext } from 'context/three-d-secure-context'
import { getUpdatePlanPurchaseType } from 'utils/tracking/calculate-checkout-properties'

import { mediaQueries, palette, fonts } from '../../styles/variables'
import { translate } from '../../utils/translation'
import { OutlineButton, PrimaryButton } from '../buttons/Buttons'
import {
  getSubscriptionOptions,
  getLifetimePrice,
  RECURLY_PUBLIC_KEY,
} from '../../utils/payment'
import { updatePlan } from '../../utils/api'
import { sendMessage } from '../../utils/functions'
import { LoadingResponsive } from '../loading/Loading'
import { Link, Text } from '../topography'
import checkIcon from '../../styles/assets/pricing/check_orange.svg'
import Icon from '../icon/Icon'
import { selectCurrentUser } from '../../state/selectors'
import { MIXPANEL_PROPS } from '../../utils/mixpanel'

import Modal, {
  ModalContent,
  ColContent,
  ModalHeader,
  ModalFooter,
} from './Modal'

const Content = styled(ColContent)({
  alignItems: 'flex-start',
  width: 'fit-content',
  [mediaQueries.mobile]: {
    height: '100%',
    width: '100%',
  },
})

const desktopStyle = {
  width: 'fit-content',
  maxWidth: '800px',
  height: 'fit-content',
  minHeight: 'fit-content',
  maxHeight: '95vh',
  overflow: 'unset',
  overflowY: 'auto',
  borderRadius: 0,
}
const mobileStyle = (fitContent) => ({
  width: '100%',
  height: fitContent ? 'fit-content' : '100vh',
  borderRadius: 0,
})

const FlexContainer = styled.div((props) => ({
  width: props.width || 'auto',
  display: 'flex',
  flexDirection: props.flexDirection || 'row',
  justifyContent: props.justifyContent || 'normal',
  alignItems: props.alignItems || 'normal',
  flexWrap: props.flexWrap || 'nowrap',
  margin: props.margin || 0,
  [mediaQueries.mobile]: {
    width: props.mobileWidth || props.width || 'auto',
    flexDirection: props.mobileFlexDirection || props.flexDirection || 'row',
    justifyContent:
      props.mobileJustifyContent || props.justifyContent || 'normal',
    alignItems: props.mobileAlignItems || props.alignItems || 'normal',
    margin: props.mobileMargin || props.margin || 0,
  },
}))

const List = styled.ul({
  padding: 0,
  width: '100%',
  margin: '10px 0 20px 0',
  listStyle: 'disc',
})

const Item = styled.li({
  fontSize: '15px',
  fontFamily: fonts.primaryLight,
  color: palette.darkGray,
  display: 'flex',
  alignItems: 'baseline',
})

const ButtonWrapper = styled.div((props) => ({
  flex: props.flex || 1,
  margin: props.margin || 0,
  [mediaQueries.mobile]: {
    flex: 1,
    margin: props.mobileMargin || 0,
  },
}))

const tier0LifetimeBulllets = [
  'special_lifetime_modal_bullet_one',
  'special_lifetime_modal_bullet_two',
  'special_lifetime_modal_bullet_two2',
  'special_lifetime_modal_bullet_three',
  'special_lifetime_modal_bullet_four',
  'special_lifetime_modal_bullet_five',
  'special_lifetime_modal_bullet_six',
]

const otherLifetimeBullets = [
  'lifetime_offer_bullet1',
  'lifetime_offer_bullet2',
  'lifetime_offer_bullet3',
]
class SpecialLifetimeModal extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = this.getInitialState()
  }

  getInitialState() {
    return {
      isProcessing: false,
      treat3DSecureError: null,
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen } = this.props
    if (!prevProps.isOpen && isOpen) {
      this.setState(this.getInitialState)
    }
  }

  getClickHandler = (countryCode, tier, updateThreeDAuth) => {
    const plan = 'lifetime'
    const subscriptions = getSubscriptionOptions({
      countryCode,
      tier,
      isLifetimeValid: true,
    })
    const getPlan = subscriptions[tier === 'tier0' ? 0 : 1]
    const { user } = this.props
    const { treat3DSecureError } = this.state

    const paymentMethod = user?.payment_method?.object

    this.setState({ isProcessing: true })

    updatePlan(getPlan?.value)
      .then((res) => {
        if (res.status === 204) {
          const purchaseType = getUpdatePlanPurchaseType(
            user.subscription.planInterval,
            plan,
            user.tier,
            tier,
          )
          trackEvent({
            event: EVENTS.CHECKOUT_SUCCESS,
            mixpanelProperties: [
              {
                tier,
                planInterval: plan,
                paymentMethod:
                  paymentMethod ||
                  MIXPANEL_PROPS.PAYMENT_METHOD.values.CREDIT_CARD,
                subscription: getPlan,
                purchaseType,
              },
            ],
          })
          sendMessage('SUBSCRIPTION_UPGRADED', {
            tier,
            plan,
            paymentMethod,
            utmParams: {
              source: this.props.source,
              medium: 'special-life-time-modal',
            },
          })
        }
        this.setState({ isProcessing: false })
        this.props.onClose()
      })
      .catch((e) => {
        if (e.code === CHECKOUT_ERRORS.THREE_D_SECURE_REQUIRED) {
          treat3DSecureError({
            tokenId: e.description.three_d_secure_action_token_id,
            onContinue: (token) =>
              updatePlan(getPlan?.value, undefined, undefined, token.id)
                .then((res) => {
                  if (res.status === 204) {
                    const purchaseType = getUpdatePlanPurchaseType(
                      user.subscription.planInterval,
                      plan,
                      user.tier,
                      tier,
                    )
                    trackEvent({
                      event: EVENTS.CHECKOUT_SUCCESS,
                      mixpanelProperties: [
                        {
                          tier,
                          planInterval: plan,
                          paymentMethod:
                            paymentMethod ||
                            MIXPANEL_PROPS.PAYMENT_METHOD.values.CREDIT_CARD,

                          subscription: getPlan,
                          purchaseType,
                        },
                      ],
                    })
                    sendMessage('SUBSCRIPTION_UPGRADED', {
                      tier,
                      plan,
                      paymentMethod,
                      utmParams: {
                        source: this.props.source,
                        medium: 'special-life-time-modal',
                      },
                    })
                  }
                })
                .catch((retryError) => {
                  console.error('error on retry with auth token', retryError)
                  this.setState({
                    treat3DSecureError: null,
                    isProcessing: false,
                  })
                  updateThreeDAuth(false)
                  this.props.onError()
                  this.props.onClose()
                }),
            setError: null,
            setShowThreeDAuth: (showThreeDAuth) =>
              updateThreeDAuth(showThreeDAuth),
          })
          this.setState({ isProcessing: false })
          this.props.onClose()
        }
      })
  }

  renderTier0 = () => {
    return (
      <ModalContent style={{ position: 'relative' }}>
        <Content>
          <Text
            fontSize="16px"
            lineHeight="24px"
            fontFamily={fonts.primary}
            boldColor={palette.darkGray}
            dangerouslySetInnerHTML={{
              __html: translate('special_lifetime_modal_includes'),
            }}
          />
          <List style={{ listStyle: 'none' }}>
            {tier0LifetimeBulllets.map((bullet, index) => (
              <Item key={index}>
                <Icon
                  src={checkIcon}
                  margin="0 10px 0 0"
                  transform="translateY(2px)"
                />
                <Text
                  color={palette.dark}
                  fontFamily={fonts.primaryLight}
                  boldColor={palette.darkGray}
                  fontSize="14px"
                  lineHeight="28px"
                  textAlign="left"
                  dangerouslySetInnerHTML={{
                    __html: translate(bullet),
                  }}
                />
              </Item>
            ))}
          </List>
          <Text
            color={palette.dark}
            fontFamily={fonts.primaryLight}
            boldColor={palette.darkGray}
            fontSize="12px"
            lineHeight="20px"
            textAlign="left"
          >
            {reactStringReplace(
              translate(
                'special_lifetime_modal_note',
                translate('special_lifetime_modal_checkout_this_article'),
              ),
              translate('special_lifetime_modal_checkout_this_article'),
              (match, i) => {
                return (
                  <Link
                    key={`title-${i}`}
                    type="direct"
                    target="_blank"
                    rel="noopener noreferrer"
                    to="https://ubersuggest.zendesk.com/hc/en-us/articles/4405442751771-Tier-0-Lifetime-Plan"
                    style={{
                      color: palette.orange,
                      textDecoration: 'underline',
                    }}
                  >
                    {match}
                  </Link>
                )
              },
            )}
          </Text>
        </Content>
      </ModalContent>
    )
  }

  renderLifetime = () => {
    return (
      <ModalContent style={{ position: 'relative' }}>
        <Content>
          <List style={{ listStyle: 'none' }}>
            {otherLifetimeBullets.map((bullet, index) => (
              <Item key={index}>
                <Icon
                  src={checkIcon}
                  margin="0 10px 0 0"
                  transform="translateY(2px)"
                />
                <Text
                  color={palette.dark}
                  fontFamily={fonts.primaryLight}
                  boldColor={palette.darkGray}
                  fontSize="14px"
                  lineHeight="28px"
                  textAlign="left"
                  dangerouslySetInnerHTML={{
                    __html: translate(bullet),
                  }}
                />
              </Item>
            ))}
          </List>
        </Content>
      </ModalContent>
    )
  }

  render() {
    const {
      user,
      showKeepMyAccount = false,
      isCancelling,
      onClose,
      onDeny,
      tier,
      fitContent,
      ...rest
    } = this.props
    const { isProcessing, treat3DSecureError } = this.state
    const lifetimePirce = getLifetimePrice(user.countryCode, tier)
    const isTier0 = tier === 'tier0'

    return (
      <ThreeDSecureContext.Consumer>
        {({ updateThreeDAuth }) => (
          <>
            <Modal
              desktopStyle={desktopStyle}
              mobileStyle={mobileStyle(fitContent)}
              parentSelector={() =>
                document.getElementById('prompt-modal-page')
              }
              {...rest}
            >
              <ModalHeader>
                <Text
                  color={palette.orange}
                  fontFamily={fonts.primaryBook}
                  fontSize="16px"
                  lineHeight="16px"
                  textAlign="center"
                  mobileFontSize="13px"
                >
                  {translate('limited_time_offer')}
                </Text>
                <Text
                  color={palette.darkGray}
                  boldColor={palette.darkGray}
                  fontFamily={fonts.primary}
                  fontSize="24px"
                  lineHeight="24px"
                  textAlign="center"
                  margin="20px 0 0 0"
                  mobileFontSize="18px"
                  dangerouslySetInnerHTML={{
                    __html: translate(
                      isTier0
                        ? 'special_lifetime_modal_heading'
                        : 'lifetime_offer_heading',
                      lifetimePirce,
                    ),
                  }}
                />
                <Text
                  color={palette.darkGray70}
                  fontFamily={fonts.primaryLight}
                  fontSize="14px"
                  lineHeight="14px"
                  textAlign="center"
                  margin="16px 0 0 0"
                  mobileFontSize="13px"
                  dangerouslySetInnerHTML={{
                    __html: translate(
                      isTier0
                        ? 'special_lifetime_modal_desp'
                        : 'lifetime_offer_sub_heading',
                    ),
                  }}
                />
              </ModalHeader>
              {isTier0 ? this.renderTier0() : this.renderLifetime()}
              <ModalFooter>
                {isCancelling || isProcessing ? (
                  <LoadingResponsive size="40px" height="50px" offsetY="5%" />
                ) : (
                  <FlexContainer mobileFlexDirection="column" width="100%">
                    {showKeepMyAccount && (
                      <OutlineButton
                        static
                        color={palette.darkGray50}
                        style={{
                          margin: '0 10px 0 0',
                          height: '55px',
                          lineHeight: '1.4em',
                          paddingRight: '14px',
                          paddingLeft: '14px',
                          flex: 1,
                        }}
                        onClick={onClose}
                      >
                        {translate('button_keep_account')}
                      </OutlineButton>
                    )}
                    <ButtonWrapper
                      margin="0 10px 0 0"
                      mobileMargin="16px 0 0 0"
                    >
                      <OutlineButton
                        static
                        color={palette.dimBlue}
                        style={{
                          margin: '0 10px 0 0',
                          height: '55px',
                          lineHeight: '1.4em',
                          padding: '0 14px',
                          minWidth: '100%',
                          background: palette.darkGray,
                        }}
                        width="100%"
                        onClick={onDeny}
                      >
                        {translate('no_thanks')}
                      </OutlineButton>
                      {showKeepMyAccount && (
                        <Text
                          fontSize="12px"
                          lineHeight="20px"
                          margin="12px 0 0 0"
                          fontFamily={fonts.primaryLight}
                          textAlign="center"
                        >
                          {translate('cancel_account')}
                        </Text>
                      )}
                    </ButtonWrapper>
                    <ButtonWrapper flex="2.5" mobileMargin="16px 0 0 0">
                      <PrimaryButton
                        theme="orange"
                        tag={translate(
                          'get_lifetime_access_button',
                          lifetimePirce,
                        )}
                        width="100%"
                        style={{
                          height: '55px',
                          lineHeight: '1.4em',
                          padding: '0 15px',
                        }}
                        onClick={() =>
                          this.getClickHandler(
                            user.countryCode,
                            tier,
                            updateThreeDAuth,
                          )
                        }
                      />
                      <Text
                        fontSize="12px"
                        lineHeight="20px"
                        margin="12px 0 0 0"
                        fontFamily={fonts.primaryLight}
                        textAlign="center"
                      >
                        {translate('non_refundable')}
                      </Text>
                    </ButtonWrapper>
                  </FlexContainer>
                )}
              </ModalFooter>
            </Modal>
            <ThreeDSecureErrorHelperWithProviders
              publicKey={RECURLY_PUBLIC_KEY}
              setTreatError={(treatErrorFunction) =>
                this.setState({ treat3DSecureError: treatErrorFunction })
              }
              treatError={treat3DSecureError}
            />
          </>
        )}
      </ThreeDSecureContext.Consumer>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  user: selectCurrentUser(),
})

const mapDispatchToProps = () => ({})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SpecialLifetimeModal)
